import type * as React from "react";

import { cx } from "../../lib/cx";
import { Icon, type IconName } from "./Icon";
import s from "./IconButton.module.css";

export type IconButtonVariant = "ghost" | "bordered" | "soft" | "solid";
export type IconButtonSize = "sm" | "md" | "lg";
/** Tinte de estado para `bordered`: fondo 50, borde 200, icono 700. */
export type IconButtonTone = "success" | "brand" | "danger" | "neutral";

const ICON_SIZE: Record<IconButtonSize, number> = { sm: 16, md: 18, lg: 20 };

export interface IconButtonProps extends Omit<React.ComponentPropsWithoutRef<"button">, "type"> {
  icon: IconName;
  /** Obligatorio: es el `aria-label` y el tooltip. El icono solo no habla. */
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Sólo con `variant="bordered"`: el botón de acción tintado de una fila (confirmar, cancelar…). */
  tone?: IconButtonTone;
  /** `square` es radio 8 en vez de píldora: los botones de acción dentro de una tabla. */
  shape?: "round" | "square";
  type?: "button" | "submit" | "reset";
  /** Con `href` es un `<a>` (o el componente de `as`). */
  href?: string;
  as?: React.ElementType;
  target?: string;
  rel?: string;
}

/** Control cuadrado sólo con icono, para barras densas. Mide lo mismo que `Button` del mismo tamaño. */
export function IconButton({
  icon,
  label,
  variant = "ghost",
  size = "md",
  tone,
  shape = "round",
  disabled = false,
  type = "button",
  href,
  as,
  target,
  rel,
  className,
  ...rest
}: IconButtonProps) {
  const Tag: React.ElementType = as ?? (href ? "a" : "button");
  const esBoton = Tag === "button";
  const propsDelTag = esBoton
    ? { type, disabled }
    : {
        href: disabled ? undefined : href,
        target,
        rel: rel ?? (target === "_blank" ? "noopener noreferrer" : undefined),
        "aria-disabled": disabled || undefined,
      };
  return (
    <Tag
      className={cx(s.boton, className)}
      data-variant={variant}
      data-size={size}
      data-tone={tone}
      data-shape={shape === "square" ? "square" : undefined}
      aria-label={label}
      title={label}
      {...propsDelTag}
      {...rest}
    >
      <Icon name={icon} size={ICON_SIZE[size]} />
    </Tag>
  );
}
